import React, { FC, useState } from 'react'
import {
  Container,
  QuadrantsContainer,
  AbsoluteContainer,
  QuadrantTagContainer,
  QuadrantTag,
  BodyQuadrant,
  ShowName,
  QuadrantCard,
  QuadrantHeader,
  QuadrantInfo,
  QuadrantName,
  QuadrantDescription,
  QuadrantStats,
  QuadrantBadge,
  QuadrantMeta,
} from './styled'
import { TeamMapProps } from 'views/containers/TeamMap'

type QuadrantCode = 'FI' | 'FD' | 'BI' | 'BD'

const QUADRANT_COPY: Record<QuadrantCode, { title: string; description: string }> = {
  FI: {
    title: 'Frontal izquierdo',
    description: 'Analisis, estructura y criterio tecnico',
  },
  FD: {
    title: 'Frontal derecho',
    description: 'Innovacion, estrategia y vision de cambio',
  },
  BI: {
    title: 'Basal izquierdo',
    description: 'Orden, continuidad y control operativo',
  },
  BD: {
    title: 'Basal derecho',
    description: 'Relacionamiento, servicio y cohesion',
  },
}

const getMetricLabel = (talents?: boolean, variant?: 'dominance' | 'talents' | 'plan') => {
  if (talents) {
    return 'Talento destacado'
  }

  if (variant === 'plan') {
    return 'Foco de desarrollo'
  }

  return 'Predominio'
}

const BrainMap: FC<TeamMapProps & { variant?: 'dominance' | 'talents' | 'plan' }> = ({ usersByQuadrant, talents, variant }) => {
  const [showFd, setShowFd] = useState(false)
  const [showFi, setShowFi] = useState(false)
  const [showBi, setShowBi] = useState(false)
  const [showBd, setShowBd] = useState(false)
  const totalUsers =
    (usersByQuadrant?.fi?.length || 0) +
    (usersByQuadrant?.fd?.length || 0) +
    (usersByQuadrant?.bi?.length || 0) +
    (usersByQuadrant?.bd?.length || 0)

  const buildMeta = (code: QuadrantCode, amount: number) => {
    const percentage = totalUsers > 0 ? Math.round((amount / totalUsers) * 100) : 0

    return {
      title: QUADRANT_COPY[code].title,
      description: QUADRANT_COPY[code].description,
      amount,
      percentage,
    }
  }

  const metricLabel = getMetricLabel(talents, variant)
  const renderQuadrantCard = ({
    code,
    left,
    isOpen,
    onToggle,
    users,
    scoreKey,
  }: {
    code: QuadrantCode
    left?: boolean
    isOpen: boolean
    onToggle: () => void
    users?: any[]
    scoreKey: 'FI' | 'FD' | 'BI' | 'BD'
  }) => {
    const meta = buildMeta(code, users?.length || 0)

    return (
      <QuadrantCard>
        <QuadrantHeader left={left}>
          <QuadrantInfo>
            <QuadrantName>{meta.title}</QuadrantName>
            <QuadrantDescription>{meta.description}</QuadrantDescription>
          </QuadrantInfo>
          <QuadrantStats>
            <QuadrantBadge>{meta.amount} personas</QuadrantBadge>
            <QuadrantMeta>
              {metricLabel}: {meta.percentage}%
            </QuadrantMeta>
          </QuadrantStats>
        </QuadrantHeader>
        <QuadrantTagContainer alignITems="center">
          <QuadrantTag show={isOpen} onClick={onToggle}>
            {isOpen ? 'Ocultar' : 'Ver detalle'}
          </QuadrantTag>
        </QuadrantTagContainer>
        <BodyQuadrant left={left}>
          {isOpen &&
            ((users?.length || 0) > 0 ? (
              users
                ?.slice()
                .sort((a: any, b: any) => Number(b[scoreKey]) - Number(a[scoreKey]))
                .map((user, index) =>
                  !talents ? (
                    <ShowName left={left} key={`${code.toLowerCase()}-${index}`}>
                      {user.full_name}
                    </ShowName>
                  ) : (
                    <ShowName left={left} key={`${code.toLowerCase()}-${index}`}>
                      {user.full_name} - {user[scoreKey]}
                    </ShowName>
                  ),
                )
            ) : (
              <ShowName left={left}>No hay evaluados en este cuadrante</ShowName>
            ))}
        </BodyQuadrant>
      </QuadrantCard>
    )
  }

  return (
    <Container>
      <QuadrantsContainer>
        {renderQuadrantCard({
          code: 'FI',
          left: true,
          isOpen: showFi,
          onToggle: () => setShowFi(!showFi),
          users: usersByQuadrant?.fi,
          scoreKey: 'FI',
        })}
        {renderQuadrantCard({
          code: 'FD',
          isOpen: showFd,
          onToggle: () => setShowFd(!showFd),
          users: usersByQuadrant?.fd,
          scoreKey: 'FD',
        })}
        {renderQuadrantCard({
          code: 'BI',
          left: true,
          isOpen: showBi,
          onToggle: () => setShowBi(!showBi),
          users: usersByQuadrant?.bi,
          scoreKey: 'BI',
        })}
        {renderQuadrantCard({
          code: 'BD',
          isOpen: showBd,
          onToggle: () => setShowBd(!showBd),
          users: usersByQuadrant?.bd,
          scoreKey: 'BD',
        })}
      </QuadrantsContainer>
      <AbsoluteContainer />
    </Container>
  )
}

export default BrainMap
